import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import React, { FC, ReactElement, useState } from 'react';
import './ProfilePage.scss';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { UserRole } from 'src/api/endpoints/users/users.types.new';
import { useGetCurrentStudentQuery } from 'src/api/endpoints/students/personal/api';
import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'src/components/Loader';
import { SecondaryButton } from 'src/components/design-system/form/buttons/SecondaryButton';
import { AvatarUploadWidget } from 'src/components/widgets/AvatarUpload';
import { ApplicationModal } from 'src/components/Modal/LegacyModal';
import { Form } from 'src/components/design-system/form';
import { TextField } from 'src/forms/DebitStudentCoins';
import { FormFieldGroup } from 'src/pages/student/introducing';
import { FormButtons } from 'src/components/design-system/form/buttons';
import { FormikErrors, useFormik } from 'formik';
import { useChangePasswordMutation } from 'src/api/endpoints/profile';
import { notifyAboutException, notifyAboutSuccess } from 'src/utils/notify';
import { ResponseError } from 'src/api';

export type ChangePasswordFormPayload = {
    oldPassword: string;
    newPassword: string;
};

export const ProfilePage: FC<AuthorizedUserProps> = (
    props: AuthorizedUserProps
): ReactElement => {
    const user = props.user;

    const [changePassword, { isLoading: isPasswordChangeLoading }] =
        useChangePasswordMutation();

    const { data: studentInformation, isLoading: isStudentInformationLoading } =
        useGetCurrentStudentQuery(
            user.role.id !== UserRole.BranchStudent ? skipToken : undefined
        );

    const [isPasswordChangeModalOpen, setPasswordChangeModalOpen] =
        useState(false);

    const formik = useFormik<Partial<ChangePasswordFormPayload>>({
        initialValues: {},
        validate: (values: Partial<ChangePasswordFormPayload>) => {
            const { oldPassword, newPassword } = values;

            const errors: FormikErrors<ChangePasswordFormPayload> = {};

            if (!oldPassword || !oldPassword.trim().length) {
                errors.oldPassword = 'Поле обязательно к заполнению';
            }

            if (!newPassword || !newPassword.trim().length) {
                errors.newPassword = 'Поле обязательно к заполнению';
            }

            if (newPassword && newPassword.trim().length < 8) {
                errors.newPassword =
                    'Пароль должен состоять как минимум из 8 символов';
            }

            if (oldPassword === newPassword) {
                errors.oldPassword = errors.newPassword =
                    'Пароли не должны совпадать';
            }

            return errors;
        },
        onSubmit: async (values: Partial<ChangePasswordFormPayload>) => {
            changePassword({ body: values as ChangePasswordFormPayload })
                .unwrap()
                .then(() => {
                    notifyAboutSuccess({
                        content: 'Пароль успешно изменен',
                    });
                    closeChangePasswordModal();
                })
                .catch((reason: ResponseError) => {
                    notifyAboutException({
                        content: reason.message,
                        sound: true,
                    });
                });
        },
    });

    const closeChangePasswordModal = () => {
        setPasswordChangeModalOpen(false);
        formik.resetForm();
    };

    return (
        <>
            <ApplicationModal
                header={'Изменение пароля'}
                isOpen={isPasswordChangeModalOpen}
                onClose={closeChangePasswordModal}
            >
                <Form formik={formik} prohibitAutoComplete={false}>
                    <FormFieldGroup>
                        <TextField
                            autoFocus={true}
                            formik={formik}
                            name={'oldPassword'}
                            autoComplete={'current-password'}
                            type={'password'}
                            label={'Текущий пароль'}
                        />
                        <TextField
                            formik={formik}
                            name={'newPassword'}
                            autoComplete={'new-password'}
                            label={'Новый пароль'}
                            type={'password'}
                        />
                    </FormFieldGroup>
                    <FormButtons
                        formik={formik}
                        isSubmitLoading={isPasswordChangeLoading}
                        submitLabel={'Изменить'}
                        onCancel={closeChangePasswordModal}
                    />
                </Form>
            </ApplicationModal>
            <StudentLayout pageName="profile-page" auth={props}>
                <section className={'content-section profile-section'}>
                    <AvatarUploadWidget />

                    <div className={'section-header'}>
                        <p className={'section-title'}>Личная информация</p>
                    </div>

                    <div className={'section-body'}>
                        <div className="profile-fields">
                            <div className="profile-field">
                                <div className="name">UID</div>
                                <div className="value">{user.id}</div>
                            </div>

                            <div className="profile-field">
                                <div className="name">Логин</div>
                                <div className="value">{user.username}</div>
                            </div>

                            <div className="profile-field">
                                <div className="name">Фамилия</div>
                                <div className="value">{user.surname}</div>
                            </div>

                            <div className="profile-field">
                                <div className="name">Имя</div>
                                <div className="value">{user.firstname}</div>
                            </div>

                            <div className="profile-field">
                                <div className="name">Отчество</div>
                                <div className="value">
                                    {user.patronymic ?? 'Не указано'}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {user.role.id === UserRole.BranchStudent && (
                    <section className={'content-section student-information'}>
                        <div className={'section-header'}>
                            <p className={'section-title'}>
                                Информация о студенте
                            </p>
                        </div>
                        <div className={'section-body'}>
                            <div className="profile-fields">
                                {!studentInformation ||
                                isStudentInformationLoading ? (
                                    <Loader />
                                ) : (
                                    <>
                                        <div className="profile-field">
                                            <div className="name">SID</div>
                                            <div className="value">
                                                {studentInformation.id}
                                            </div>
                                        </div>

                                        <div className="profile-field">
                                            <div className="name">
                                                Номер договора
                                            </div>
                                            <div className="value">
                                                {studentInformation.contract}
                                            </div>
                                        </div>

                                        <div className="profile-field">
                                            <div className="name">Статус</div>
                                            <div className="value">
                                                {studentInformation.status.name}
                                            </div>
                                        </div>

                                        <div className="profile-field">
                                            <div className="name">Группа</div>
                                            <div className="value">
                                                {studentInformation.group.name}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </section>
                )}
                <section className={'content-section security-section'}>
                    <div className={'section-header'}>
                        <p className={'section-title'}>Безопасность</p>
                    </div>

                    <div className={'section-body'}>
                        <div className="profile-fields">
                            <div className="profile-field">
                                <div className="name">Пароль</div>
                                <div className="value">
                                    <SecondaryButton
                                        label={'Изменить'}
                                        onClick={() => {
                                            setPasswordChangeModalOpen(true);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                {/*<div className="card mt-24">*/}
                {/*    <div className="header flex align-items-center justify-content-between profile-bg">*/}
                {/*        <div className="title big">Личный кабинет</div>*/}
                {/*    </div>*/}
                {/*    {isLoading || isWhoamiLoading || !whoami ? (*/}
                {/*        <Loader />*/}
                {/*    ) : (*/}
                {/*        <div className="content">*/}
                {/*            <div className="profile flex flex-column align-items-center mt-24">*/}
                {/*                <AvatarUploadWidget />*/}
                {/*                {profileCompleted ? (*/}
                {/*                    <>*/}
                {/*                        <div className="mt-48 fz-40 text-black">*/}
                {/*                            {getFullnameWithoutPatronymic({*/}
                {/*                                name: whoami.firstname,*/}
                {/*                                surname: whoami.surname,*/}
                {/*                                patronymic: whoami.patronymic,*/}
                {/*                            })}*/}
                {/*                        </div>*/}
                {/*                        <small className="date mt-24">*/}
                {/*                            {data.country}, {data.city}, {diff}{' '}*/}
                {/*                            {num_word(diff, ['год', 'года', 'лет'])}*/}
                {/*                        </small>*/}
                {/*                    </>*/}
                {/*                ) : null}*/}
                {/*            </div>*/}
                {/*            <form*/}
                {/*                onSubmit={formik.handleSubmit}*/}
                {/*                className="grid form mt-64"*/}
                {/*            >*/}
                {/*                <div className="field lg:col-3 md:col-6 col-12">*/}
                {/*                    <span className="p-float-label">*/}
                {/*                        <InputText*/}
                {/*                            id="fio"*/}
                {/*                            value={formik.values.fio}*/}
                {/*                            autoComplete={uuidV4()}*/}
                {/*                            onChange={formik.handleChange}*/}
                {/*                            className={*/}
                {/*                                classNames({*/}
                {/*                                    'p-invalid':*/}
                {/*                                        isFormFieldValid('fio'),*/}
                {/*                                }) + ' w-full'*/}
                {/*                            }*/}
                {/*                        />*/}
                {/*                        <label*/}
                {/*                            htmlFor="fio"*/}
                {/*                            className={classNames({*/}
                {/*                                'p-error': isFormFieldValid('fio'),*/}
                {/*                            })}*/}
                {/*                        >*/}
                {/*                            ФИО*/}
                {/*                        </label>*/}
                {/*                    </span>*/}
                {/*                    {getFormErrorMessage('fio')}*/}
                {/*                </div>*/}
                {/*                <div className="field lg:col-3 md:col-6 col-12">*/}
                {/*                    <span className="p-float-label">*/}
                {/*                        <Calendar*/}
                {/*                            id="birthday"*/}
                {/*                            value={new Date(formik.values.birthday)}*/}
                {/*                            onChange={formik.handleChange}*/}
                {/*                            dateFormat="dd-mm-yy"*/}
                {/*                            className={*/}
                {/*                                classNames({*/}
                {/*                                    'p-invalid':*/}
                {/*                                        isFormFieldValid(*/}
                {/*                                            'birthday'*/}
                {/*                                        ),*/}
                {/*                                }) + ' w-full'*/}
                {/*                            }*/}
                {/*                        />*/}
                {/*                        <label*/}
                {/*                            htmlFor="birthday"*/}
                {/*                            className={classNames({*/}
                {/*                                'p-error':*/}
                {/*                                    isFormFieldValid('birthday'),*/}
                {/*                            })}*/}
                {/*                        >*/}
                {/*                            Дата рождения*/}
                {/*                        </label>*/}
                {/*                    </span>*/}
                {/*                    {getFormErrorMessage('birthday')}*/}
                {/*                </div>*/}
                {/*                <div className="field lg:col-3 md:col-6 col-12">*/}
                {/*                    <span className="p-float-label">*/}
                {/*                        <InputText*/}
                {/*                            id="email"*/}
                {/*                            value={formik.values.email}*/}
                {/*                            onChange={formik.handleChange}*/}
                {/*                            autoComplete={uuidV4()}*/}
                {/*                            className={*/}
                {/*                                classNames({*/}
                {/*                                    'p-invalid':*/}
                {/*                                        isFormFieldValid('email'),*/}
                {/*                                }) + ' w-full'*/}
                {/*                            }*/}
                {/*                        />*/}
                {/*                        <label*/}
                {/*                            htmlFor="email"*/}
                {/*                            className={classNames({*/}
                {/*                                'p-error':*/}
                {/*                                    isFormFieldValid('email'),*/}
                {/*                            })}*/}
                {/*                        >*/}
                {/*                            Email*/}
                {/*                        </label>*/}
                {/*                    </span>*/}
                {/*                    {getFormErrorMessage('email')}*/}
                {/*                </div>*/}
                {/*                <div className="field lg:col-3 md:col-6 col-12">*/}
                {/*                    <span className="p-float-label">*/}
                {/*                        <InputMask*/}
                {/*                            id="phone"*/}
                {/*                            mask="+7 (999) 999 99 99"*/}
                {/*                            value={formik.values.phone}*/}
                {/*                            onChange={formik.handleChange}*/}
                {/*                            autoComplete={uuidV4()}*/}
                {/*                            className={*/}
                {/*                                classNames({*/}
                {/*                                    'p-invalid':*/}
                {/*                                        isFormFieldValid('phone'),*/}
                {/*                                }) + ' w-full'*/}
                {/*                            }*/}
                {/*                        />*/}
                {/*                        <label*/}
                {/*                            htmlFor="phone"*/}
                {/*                            className={classNames({*/}
                {/*                                'p-error':*/}
                {/*                                    isFormFieldValid('phone'),*/}
                {/*                            })}*/}
                {/*                        >*/}
                {/*                            Телефон*/}
                {/*                        </label>*/}
                {/*                    </span>*/}
                {/*                    {getFormErrorMessage('phone')}*/}
                {/*                </div>*/}
                {/*                <div className="field lg:col-3 md:col-6 col-12">*/}
                {/*                    <span className="p-float-label">*/}
                {/*                        <InputText*/}
                {/*                            id="country"*/}
                {/*                            value={formik.values.country}*/}
                {/*                            onChange={formik.handleChange}*/}
                {/*                            autoComplete={uuidV4()}*/}
                {/*                            className={*/}
                {/*                                classNames({*/}
                {/*                                    'p-invalid':*/}
                {/*                                        isFormFieldValid('country'),*/}
                {/*                                }) + ' w-full'*/}
                {/*                            }*/}
                {/*                        />*/}
                {/*                        <label*/}
                {/*                            htmlFor="country"*/}
                {/*                            className={classNames({*/}
                {/*                                'p-error':*/}
                {/*                                    isFormFieldValid('country'),*/}
                {/*                            })}*/}
                {/*                        >*/}
                {/*                            Страна*/}
                {/*                        </label>*/}
                {/*                    </span>*/}
                {/*                    {getFormErrorMessage('country')}*/}
                {/*                </div>*/}
                {/*                <div className="field lg:col-3 md:col-6 col-12">*/}
                {/*                    <span className="p-float-label">*/}
                {/*                        <InputText*/}
                {/*                            id="city"*/}
                {/*                            value={formik.values.city}*/}
                {/*                            onChange={formik.handleChange}*/}
                {/*                            autoComplete={uuidV4()}*/}
                {/*                            className={*/}
                {/*                                classNames({*/}
                {/*                                    'p-invalid':*/}
                {/*                                        isFormFieldValid('city'),*/}
                {/*                                }) + ' w-full'*/}
                {/*                            }*/}
                {/*                        />*/}
                {/*                        <label*/}
                {/*                            htmlFor="city"*/}
                {/*                            className={classNames({*/}
                {/*                                'p-error': isFormFieldValid('city'),*/}
                {/*                            })}*/}
                {/*                        >*/}
                {/*                            Город*/}
                {/*                        </label>*/}
                {/*                    </span>*/}
                {/*                    {getFormErrorMessage('city')}*/}
                {/*                </div>*/}
                {/*                <div className="field lg:col-3 md:col-6 col-12">*/}
                {/*                    <span className="p-float-label">*/}
                {/*                        <InputText*/}
                {/*                            id="address"*/}
                {/*                            value={formik.values.address}*/}
                {/*                            onChange={formik.handleChange}*/}
                {/*                            autoComplete={uuidV4()}*/}
                {/*                            className={*/}
                {/*                                classNames({*/}
                {/*                                    'p-invalid':*/}
                {/*                                        isFormFieldValid('address'),*/}
                {/*                                }) + ' w-full'*/}
                {/*                            }*/}
                {/*                        />*/}
                {/*                        <label*/}
                {/*                            htmlFor="address"*/}
                {/*                            className={classNames({*/}
                {/*                                'p-error':*/}
                {/*                                    isFormFieldValid('address'),*/}
                {/*                            })}*/}
                {/*                        >*/}
                {/*                            Улица, дом*/}
                {/*                        </label>*/}
                {/*                    </span>*/}
                {/*                    {getFormErrorMessage('address')}*/}
                {/*                </div>*/}
                {/*                {profileCompleted ? (*/}
                {/*                    <div className="field lg:col-3 md:col-6 col-12">*/}
                {/*                        <span className="p-float-label">*/}
                {/*                            <InputText*/}
                {/*                                readOnly*/}
                {/*                                id="contractNumber"*/}
                {/*                                value={formik.values.contractNumber}*/}
                {/*                                onChange={formik.handleChange}*/}
                {/*                                className={*/}
                {/*                                    classNames({*/}
                {/*                                        'p-invalid':*/}
                {/*                                            isFormFieldValid(*/}
                {/*                                                'contractNumber'*/}
                {/*                                            ),*/}
                {/*                                    }) + ' w-full'*/}
                {/*                                }*/}
                {/*                            />*/}
                {/*                            <label*/}
                {/*                                htmlFor="contractNumber"*/}
                {/*                                className={classNames({*/}
                {/*                                    'p-error':*/}
                {/*                                        isFormFieldValid(*/}
                {/*                                            'contractNumber'*/}
                {/*                                        ),*/}
                {/*                                })}*/}
                {/*                            >*/}
                {/*                                № договора*/}
                {/*                            </label>*/}
                {/*                        </span>*/}
                {/*                        {getFormErrorMessage('contractNumber')}*/}
                {/*                    </div>*/}
                {/*                ) : (*/}
                {/*                    <div className="field flex flex-column lg:col-3 md:col-6 col-12">*/}
                {/*                        <span className="p-float-label p-input-icon-right">*/}
                {/*                            {visible ? (*/}
                {/*                                <i*/}
                {/*                                    className="pi pi-eye"*/}
                {/*                                    onClick={toggleVisible}*/}
                {/*                                />*/}
                {/*                            ) : (*/}
                {/*                                <i*/}
                {/*                                    className="pi pi-eye-slash"*/}
                {/*                                    onClick={toggleVisible}*/}
                {/*                                />*/}
                {/*                            )}*/}
                {/*                            <InputText*/}
                {/*                                id="password"*/}
                {/*                                type={visible ? 'text' : 'password'}*/}
                {/*                                value={formik.values.password}*/}
                {/*                                onChange={formik.handleChange}*/}
                {/*                                className={classNames(*/}
                {/*                                    {*/}
                {/*                                        'p-invalid':*/}
                {/*                                            isFormFieldValid(*/}
                {/*                                                'password'*/}
                {/*                                            ),*/}
                {/*                                    },*/}
                {/*                                    'w-full'*/}
                {/*                                )}*/}
                {/*                            />*/}
                {/*                            <label*/}
                {/*                                htmlFor="password"*/}
                {/*                                className={classNames({*/}
                {/*                                    'p-error':*/}
                {/*                                        isFormFieldValid(*/}
                {/*                                            'password'*/}
                {/*                                        ),*/}
                {/*                                })}*/}
                {/*                            >*/}
                {/*                                Пароль*/}
                {/*                            </label>*/}
                {/*                        </span>*/}
                {/*                        {getFormErrorMessage('password')}*/}
                {/*                    </div>*/}
                {/*                )}*/}
                {/*                <div className="col-12 flex justify-content-center">*/}
                {/*                    <Button type="submit" loading={buttonLoading}>*/}
                {/*                        Сохранить*/}
                {/*                    </Button>*/}
                {/*                </div>*/}
                {/*            </form>*/}
                {/*        </div>*/}
                {/*    )}*/}
                {/*    <Toast ref={toastRef} />*/}
                {/*</div>*/}
            </StudentLayout>
        </>
    );
};
